import type { FunctionArgs } from "convex/server"
import type { Id, Priority, TicketCategory, TicketSource, api } from "@/lib/api"
import { isWellFormedText } from "../../../../convex/lib/feedbackPayload"
import { MAX_FILTER_VALUES } from "../../../../convex/lib/limits"
import { UNASSIGNED } from "../../../../convex/lib/validators"

/**
 * The filters of the Tickets page as the URL holds them, and the way from
 * there to what the server takes. Pure, so every rule is tested directly:
 * the URL is the only place the filters live, and a link someone shares, a
 * reload and the back button all have to give the same list.
 */

/** The groups of the page, in the order they are drawn. Triage has its own page. */
export const LISTED_STATUSES = ["open", "waiting", "resolved", "declined", "duplicate"] as const
export type ListedStatus = (typeof LISTED_STATUSES)[number]

const CATEGORIES = ["bug", "feature", "question", "praise"] as const satisfies readonly TicketCategory[]
const PRIORITIES = [
  "none",
  "low",
  "medium",
  "high",
  "urgent",
] as const satisfies readonly Priority[]
const SOURCES = ["sdk", "api", "portal", "manual", "github"] as const satisfies readonly TicketSource[]

/** In `assignee`, the value for tickets assigned to nobody. */
export const NO_ASSIGNEE = UNASSIGNED

/** What `tickets.list` takes as `filters`, and what a saved view stores. */
export type TicketFilters = FunctionArgs<typeof api.tickets.list>["filters"]

/**
 * The search params of the Tickets page. Every key is optional and none is
 * ever an empty list. `status` is not a filter the server knows: the page is
 * grouped by status, so it says which groups are shown.
 */
export type TicketsSearch = {
  status?: ListedStatus[]
  category?: TicketCategory[]
  priority?: Priority[]
  /** User ids, and `NO_ASSIGNEE`. */
  assignee?: string[]
  /** Label ids. */
  label?: string[]
  source?: TicketSource[]
  /** One contact id: a requester's tickets. Set from the ticket page. */
  contact?: string
}

// What the server takes in an assignee filter: `cleanFilters` in
// convex/lib/ticketFilters.ts refuses the whole list for one value outside this.
const USER_ID_MAX = 128
const isUserId = (value: string) =>
  value.length > 0 && value.length <= USER_ID_MAX && isWellFormedText(value)

// A Convex document id is lowercase letters and digits. Whether it names a
// label or a contact only the server can say, but what could not be an id at
// all never reaches it.
const DOCUMENT_ID = /^[a-z0-9]{16,64}$/
const isDocumentId = (value: string) => DOCUMENT_ID.test(value)

/**
 * A list as the URL may carry it: an array, one value, or values between
 * commas for a link written by hand. Anything that is not an accepted value
 * is left out, as is a repeat and whatever comes after the most the server
 * takes of one kind.
 */
function listOf<Value extends string>(
  raw: unknown,
  accepts: (value: string) => boolean,
): Value[] | undefined {
  const candidates: unknown[] =
    typeof raw === "string" ? raw.split(",") : Array.isArray(raw) ? raw : []
  const kept: string[] = []
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue
    const value = candidate.trim()
    if (!accepts(value) || kept.includes(value)) continue
    kept.push(value)
    if (kept.length === MAX_FILTER_VALUES) break
  }
  return kept.length > 0 ? (kept as Value[]) : undefined
}

/** The values of a fixed set, in the set's own order whatever order they came in. */
function choiceOf<Value extends string>(
  raw: unknown,
  choices: readonly Value[],
): Value[] | undefined {
  const given = listOf<Value>(raw, (value) => (choices as readonly string[]).includes(value))
  return given ? choices.filter((choice) => given.includes(choice)) : undefined
}

/**
 * `validateSearch` of the route. Whatever the URL says, this returns filters
 * the page can use: an unknown key or value is dropped, never thrown on.
 */
export function parseTicketsSearch(raw: Record<string, unknown>): TicketsSearch {
  const search: TicketsSearch = {}
  const status = choiceOf(raw.status, LISTED_STATUSES)
  if (status) search.status = status
  const category = choiceOf(raw.category, CATEGORIES)
  if (category) search.category = category
  const priority = choiceOf(raw.priority, PRIORITIES)
  if (priority) search.priority = priority
  const assignee = listOf<string>(raw.assignee, isUserId)
  if (assignee) search.assignee = assignee
  const label = listOf<string>(raw.label, isDocumentId)
  if (label) search.label = label
  const source = choiceOf(raw.source, SOURCES)
  if (source) search.source = source
  if (typeof raw.contact === "string" && isDocumentId(raw.contact.trim())) {
    search.contact = raw.contact.trim()
  }
  return search
}

/** The `filters` argument of `tickets.list` for a URL. Keys are always in this order. */
export function filtersOf(search: TicketsSearch): TicketFilters {
  const filters: TicketFilters = {}
  if (search.category) filters.category = search.category
  if (search.priority) filters.priority = search.priority
  if (search.assignee) filters.assigneeId = search.assignee
  if (search.label) filters.labelId = search.label as Id<"labels">[]
  if (search.contact) filters.contactId = search.contact as Id<"contacts">
  if (search.source) filters.source = search.source
  return filters
}

/**
 * The search params that give these filters: what picking a saved view puts
 * in the URL. It goes through the same parser as a URL does, so a view can
 * put nothing there that a link could not.
 */
export function searchOf(
  filters: TicketFilters,
  statuses?: readonly ListedStatus[],
): TicketsSearch {
  return parseTicketsSearch({
    status: statuses,
    category: filters.category,
    priority: filters.priority,
    assignee: filters.assigneeId,
    label: filters.labelId,
    source: filters.source,
    contact: filters.contactId,
  })
}

/** Whether the filters narrow the list at all. */
export function hasFilters(filters: TicketFilters): boolean {
  return Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== undefined,
  )
}

function sameValues(a: readonly string[] | undefined, b: readonly string[] | undefined) {
  const left = new Set(a)
  const right = new Set(b)
  if (left.size !== right.size) return false
  for (const value of left) if (!right.has(value)) return false
  return true
}

/**
 * Whether two sets of filters give the same list. The order values were
 * picked in does not count, and an empty list is no list.
 */
export function sameFilters(a: TicketFilters, b: TicketFilters): boolean {
  return (
    sameValues(a.category, b.category) &&
    sameValues(a.priority, b.priority) &&
    sameValues(a.assigneeId, b.assigneeId) &&
    sameValues(a.labelId, b.labelId) &&
    sameValues(a.source, b.source) &&
    (a.contactId ?? null) === (b.contactId ?? null)
  )
}

/** What a saved view holds: filters, and the groups shown when not all of them. */
export type ViewContent = { filters: TicketFilters; statuses?: readonly ListedStatus[] }

/** Whether the URL has anything a view could hold. */
export function hasViewContent(search: TicketsSearch): boolean {
  return search.status !== undefined || hasFilters(filtersOf(search))
}

/**
 * The saved view the URL is showing, if it is one: the first whose filters
 * and groups are the URL's.
 */
export function activeViewOf<View extends ViewContent>(
  views: readonly View[],
  search: TicketsSearch,
): View | undefined {
  if (!hasViewContent(search)) return undefined
  const filters = filtersOf(search)
  return views.find(
    (view) => sameFilters(view.filters, filters) && sameValues(view.statuses, search.status),
  )
}

/** The ids `<FilterBar>` knows the filters by. They are the search params' own names. */
export type FilterId = keyof TicketsSearch

/** The URL as `<FilterBar>` takes it: every kind a list, the requester a list of one. */
export function filterValueOf(search: TicketsSearch): Record<string, string[]> {
  const value: Record<string, string[]> = {}
  if (search.status) value.status = search.status
  if (search.category) value.category = search.category
  if (search.priority) value.priority = search.priority
  if (search.assignee) value.assignee = search.assignee
  if (search.label) value.label = search.label
  if (search.source) value.source = search.source
  if (search.contact) value.contact = [search.contact]
  return value
}

/** And back: what `<FilterBar>` reports, as search params. */
export function searchOfFilterValue(value: Record<string, string[]>): TicketsSearch {
  return parseTicketsSearch({ ...value, contact: value.contact?.[0] })
}
